<script setup lang="ts">
// Built on Bootstrap's own accordion markup/CSS (.accordion-item/.accordion-header/.accordion-button/
// .accordion-collapse/.accordion-body — chevron and rotation included via `::after` + the `.collapsed`
// class). The project only imports bootstrap's CSS, never its JS, so the collapse state below is
// entirely driven by our own Vue state, never bootstrap's Collapse plugin — no risk of conflict.
export interface SancareAccordionItemProps {
  title: string
  isCollapsible?: boolean
  // 'header' (default): clicking anywhere on the header toggles the accordion, matching plain
  // Bootstrap accordion behavior.
  // 'arrow': only the chevron toggles the accordion ; the title has no built-in action, for
  // callers whose title already has its own action (e.g. navigating to a document) that
  // shouldn't also collapse the content.
  toggleTarget?: 'header' | 'arrow'
  // 'boxed' (default): bootstrap's own border/radius and padding.
  // 'flush': bootstrap's `.accordion-flush` equivalent, for accordions sitting edge-to-edge
  // in their container.
  variant?: 'boxed' | 'flush'
}

const props = withDefaults(defineProps<SancareAccordionItemProps>(), {
  isCollapsible: true,
  toggleTarget: 'header',
  variant: 'boxed',
})

// External code reacts to expand/collapse solely through `v-model:isExpanded` — no dedicated
// event, since defineModel already emits `update:isExpanded` on every change.
const isExpanded = defineModel<boolean>('isExpanded', { default: true })

const emit = defineEmits<{
  // Fired instead of toggling when toggleTarget is 'arrow', so callers can wire their own
  // action (e.g. navigating to a document) on the title without it also collapsing the content.
  // Carries the native click event so that action can tell a plain click from a ctrl/middle click
  // (e.g. to open the target in a new tab).
  (e: 'title-click', event: MouseEvent): void
}>()

function toggle(): void {
  if (!props.isCollapsible) {
    return
  }
  isExpanded.value = !isExpanded.value
}

function handleTitleClick(event: MouseEvent): void {
  if (props.toggleTarget === 'arrow') {
    emit('title-click', event)
    return
  }
  toggle()
}

// A middle click never toggles, even with toggleTarget 'header' — there's no navigation action to
// open in a new tab there (title-click only fires with 'arrow'), so a middle click is simply a
// no-op instead of unexpectedly toggling the accordion.
function handleTitleMiddleClick(event: MouseEvent): void {
  if (props.toggleTarget === 'arrow') {
    emit('title-click', event)
  }
}
</script>

<template>
  <div
    class="accordion accordion-item"
    :class="{ 'accordion-item--flush': variant === 'flush' }"
  >
    <h2 class="accordion-header d-flex align-items-stretch">
      <!-- Span (not div, button content model is phrasing-only) stops its click from bubbling, so it never double-fires the toggle above (needed for toggleTarget: 'arrow'). -->
      <component
        :is="isCollapsible ? 'button' : 'div'"
        :type="isCollapsible ? 'button' : undefined"
        class="accordion-button"
        :class="{ collapsed: isCollapsible && !isExpanded }"
        :aria-expanded="isCollapsible ? isExpanded : undefined"
        @click.stop="toggle"
      >
        <span
          class="accordion-button__title"
          @click.stop="handleTitleClick"
          @click.middle.stop="handleTitleMiddleClick"
        >
          {{ title }}
        </span>
      </component>
      <slot name="right" />
    </h2>
    <transition name="slide-fade">
      <div
        v-if="!isCollapsible || isExpanded"
        class="accordion-collapse"
      >
        <div class="accordion-body">
          <slot />
        </div>
      </div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
.accordion-button {
  font-weight: bold;
  // Padding moved onto .accordion-button__title below, so the title's own click/hit box covers the full header height and reaches the button's right edge.
  padding: 0;
  align-items: stretch;

  // Chevron placed before the title text (bootstrap's default ::after renders after content).

  &::after {
    order: -1;
    // Re-centered vertically since align-items: stretch above would otherwise default this fixed-height icon to flex-start.
    align-self: center;
    margin: 0 10px 0 var(--bs-accordion-btn-padding-x);
  }
}

.accordion-button__title {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  padding: var(--bs-accordion-btn-padding-y) var(--bs-accordion-btn-padding-x) var(--bs-accordion-btn-padding-y) 0;
}

// Bootstrap's default padding on the collapsed content — removed, this component's callers
// handle their own body spacing.

.accordion-item {
  --bs-accordion-body-padding-x: 0;
  --bs-accordion-body-padding-y: 0;
}

.accordion-header {
  --bs-accordion-btn-icon-width: .75rem;
  // Bootstrap's default chevron is a thin 1px stroke, and swaps to a blue one when expanded
  // (`--bs-accordion-btn-active-icon`) — thickened and kept the same black in both states.
  --bs-accordion-btn-icon: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='%23212529' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpath d='m2 5 6 6 6-6'/%3e%3c/svg%3e");
  --bs-accordion-btn-active-icon: var(--bs-accordion-btn-icon);
  // Bootstrap gives the expanded (`:not(.collapsed)`) button an "active" blue text/background —
  // this component always keeps the header's default black-on-white look, expanded or not.
  // Overriding these (rather than the `.accordion-button:not(.collapsed)` rule itself) keeps
  // bootstrap's own box-shadow, which draws the border between the header and the body below.
  --bs-accordion-active-color: var(--bs-accordion-btn-color);
  --bs-accordion-active-bg: var(--bs-accordion-btn-bg);
  // Bootstrap's blue focus/active ring on the accordion buttons — not wanted here.
  --bs-accordion-btn-focus-box-shadow: none;
}

// A focused expanded button keeps bootstrap's `:focus` box-shadow (equal specificity, declared later) from wiping the `:not(.collapsed)` separator.

.accordion-button:focus:not(.collapsed) {
  box-shadow: inset 0 calc(-1 * var(--bs-accordion-border-width)) 0 var(--bs-accordion-border-color);
}

// The header/body separator is bootstrap's own box-shadow on the expanded button, drawn from
// the same --bs-accordion-border-* variables as the item's outer border — zeroing them here
// removes both at once instead of fighting bootstrap's `border`/`box-shadow` properties directly.

.accordion-item--flush {
  --bs-accordion-border-width: 0;
  --bs-accordion-border-radius: 0;
  --bs-accordion-inner-border-radius: 0;
  // Tighter than bootstrap's defaults (1rem / 1.25rem). `.25rem` is bootstrap's own spacer-1 —
  // about as tight as it gets while staying on the spacing scale.
  --bs-accordion-btn-padding-y: .5rem;
  --bs-accordion-btn-padding-x: .25rem;
}
</style>
